import { Component, Injector, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { BehaviorSubject, EMPTY, Observable, timer } from 'rxjs';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { take, takeUntil, tap } from 'rxjs/operators';
import {
  FcAddressListItemDto,
  FcCorporationListItemDto,
  FcInsuranceContactListItemDto,
  FcInsurancePlanDataDto,
  FormGroupValidationRules,
  RelEmployerInsurancePlanDto,
  RelEmployerItem,
} from 'webcommon/shared';
import { FindControlIdService } from 'webcommon/legacy-common';
import {
  ApAlertService,
  ApModalService,
  ApUndoableOperationService
} from 'webcommon/core-ui';

import { FcListValidationService } from '../../generic/fc-list-validation/fc-list-validation.service';
import { FormGroupExtended, FcItemListBaseComponent } from '../../generic';
import { FcItemModalMode, FcItemModalContext } from '../../../models';
import {
  FcNewInsurancePeriodListModalComponent
} from '../../fc-item-modals/fc-new-insurance-period-list-modal/fc-new-insurance-period-list-modal.component';
import { ApFindControlService } from '../../ap-findcontrol.service';

interface ResultModalItem {
  isSuccess: boolean;
  reason: number;
  resultValue: {
    result: RelEmployerItem;
  };
}

@Component({
  providers: [ApAlertService, FcListValidationService],
  selector: 'fc-workers-comp',
  templateUrl: './fc-workers-comp.component.html',
  styleUrls: ['./fc-workers-comp.component.less']
})
export class FcWorkersCompComponent extends FcItemListBaseComponent implements OnInit, OnChanges {
  @Input() form: FormGroup;
  @Input() baseId: string;
  @Input() isSubmitted = false;
  @Input() itemContext: FcItemModalContext;
  @Input() listItem: RelEmployerInsurancePlanDto[];

  public addressListItem: FcAddressListItemDto = {
    Address1: '',
    Address2: '',
    City: '',
    State: '',
    Zip: '',
    CountyName: '',
    CountyCode: '',
  };

  public undoComponentId: number;
  public selectedIndex: number | null;
  public showRowDetails = true;
  public selectedFormGroup = new FormGroup({});

  public workerCompFormArray = new FormArray<FormGroup>([]);
  public addressGroup = new FormGroupExtended({});
  public disableAddressListControls: BehaviorSubject<boolean[] | null> = new BehaviorSubject<boolean[] | null>([true, false]);

  public selectedInsurancePlanPks: string[] = [];
  public selectedInsuranceContactPks: string[] = [];

  public listItemDefaultValidation: FormGroupValidationRules = {
    ExternalId: {
      MaxLength: 25,
    },
    Name: {
      IsRequired: true,
      MaxLength: 100,
    },
    Contact: {
      MaxLength: 50,
    },
    InsurancePayer: {
      IsRequired: true,
    },
    CorporationUid: {
      IsRequired: true,
    },
    Notes: {
      IsRequired: false,
      MaxLength: 8000,
    },
    Email: {
      MaxLength: 100,
    },
  };

  constructor(
    injector: Injector,
    protected apUndoableOperationService: ApUndoableOperationService,
    private apModalService: ApModalService,
    private apFindControlService: ApFindControlService,
    private snackBar: MatSnackBar,
  ) {
    super(injector);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.itemContext != null) {
      this.fcListValidation.initValidation(this.itemContext, this.listItemDefaultValidation);
    }
    if (changes.form != null) {
      this.addControlsToForm();
      this.addControlsToForm();
    }
    if (changes.itemContext != null || changes.form != null || changes.listItem != null) {
      this.setForm();
    }
  }

  public ngOnInit(): void {
    this.addControlsToForm();
    this.disableAddressGroup();
    this.fcListValidation.initValidation(this.itemContext, this.listItemDefaultValidation);
    this.undoComponentId = this.apUndoableOperationService.registerComponent();
  }

  public setForm(): void {
    this.listItem?.forEach((el: RelEmployerInsurancePlanDto) => {
      this.addWorkerCompRow(el);
    });

    if (this.listItem?.length) {
      this.choseTableItem(0);
    }

    this.sortInsuranceDates(this.workerCompFormArray.value);
  }

  public onInsurancePlanDataPopulate(insurancePlanArray: FcInsurancePlanDataDto[]): void {
    const insurancePlanData: FcInsurancePlanDataDto = { ...insurancePlanArray[0] };

    if (insurancePlanData && insurancePlanArray[0]) {
      this.apFindControlService
        .getItem<FcCorporationListItemDto>(FindControlIdService.ViewPayerPlan, insurancePlanData.Pk)
        .pipe(
          takeUntil(this.onDestroy$)
        )
        .subscribe((fullInsuranceData: FcCorporationListItemDto) => {
          this.addressListItem = {
            Address1: insurancePlanData.PlanAddress1,
            Address2: insurancePlanData.PlanAddress2,
            ZipUid: fullInsuranceData?.Address?.ZipUid,
            Zip: insurancePlanData.Zip,
            State: insurancePlanData.PlanState,
            City: insurancePlanData.PlanCity,
          };
          this.addressGroup = new FormGroupExtended({});
          if (this.selectedIndex !== null) {
            this.workerCompFormArray.at(this.selectedIndex)?.get('addressData')?.setValue(this.addressListItem);
            this.workerCompFormArray.at(this.selectedIndex)?.get('phoneAddress')?.setValue(insurancePlanData.PhoneNumber);
          }
        });

    } else {
      this.addressListItem = {};
      if (this.selectedIndex !== null) {
        this.workerCompFormArray.at(this.selectedIndex)?.get('phoneAddress')?.setValue('');
        this.workerCompFormArray.at(this.selectedIndex)?.get('addressData')?.setValue({});
      }
    }
  }

  public onInsuranceContactDataPopulate(insuranceContactArray: FcInsuranceContactListItemDto[]): void {
    const insuranceContactData: FcInsuranceContactListItemDto = { ...insuranceContactArray[0] };

    if (insuranceContactData && insuranceContactArray[0]) {
      if (this.selectedIndex !== null) {
        this.workerCompFormArray.at(this.selectedIndex).get('InsuranceContactData')?.setValue({
          phoneNumber: insuranceContactData?.['Phone1.PhoneNumber'] ?? '',
          email1: insuranceContactData?.Email1 ?? '',
          email2: insuranceContactData?.Email2 ?? ''
        });
      }
    } else {
      if (this.selectedIndex !== null) {
        this.workerCompFormArray.at(this.selectedIndex)?.get('InsuranceContactData')?.setValue({});
      }

    }
  }

  public choseTableItem(index: number): void {
    this.showRowDetails = false;
    this.setSelectedIndex(index);

    timer(0)
      .pipe(take(1))
      .subscribe(() => {
        this.showRowDetails = true;
      });
  }

  private addControlsToForm(): void {
    this.form.addControl('WorkerCompFormArray', this.workerCompFormArray);
  }

  private addWorkerCompRow(result: RelEmployerInsurancePlanDto): void {
    this.workerCompFormArray.push(
      new FormGroup({
        RelEmployerInsurancePlanUid: new FormControl(result.RelEmployerInsurancePlanUid),
        CorporationUid: new FormControl(result.CorporationUid),
        InsurancePlan: new FormControl(result.InsurancePlanUid ? [result.InsurancePlanUid] : []),
        InsuranceContact: new FormControl(result.InsuranceContactUid ? [result.InsuranceContactUid] : []),
        InsuranceContactData: new FormControl(''),
        EffectiveDate: new FormControl(result.EffectiveDate),
        ExpirationDate: new FormControl(result.ExpirationDate),
        localId: new FormControl(result.localId),
        addressData: new FormControl(result.Address),
        phoneAddress: new FormControl(result.PhoneNumber ?? '')
      })
    );
    this.form.markAsDirty();
  }

  private setDataItems(item: ResultModalItem, isModify = false): void {
    const result = item.resultValue.result;
    const tableItem: RelEmployerInsurancePlanDto = {
      EffectiveDate: result.EffectiveDate,
      ExpirationDate: result.ExpirationDate,
      localId: result.id
    };

    if (!isModify) {
      this.addWorkerCompRow(tableItem);
      if (this.selectedIndex === undefined) {
        this.choseTableItem(0);
      }
    } else {
      const itemIndexForModify = this.workerCompFormArray.value
        .findIndex((element: { localId: string }) => element.localId === tableItem.localId);
      const previousElement = this.workerCompFormArray.value[itemIndexForModify - 1];
      this.choseTableItem(itemIndexForModify);
      if (previousElement) {
        const nextDay = tableItem.ExpirationDate ? new Date(tableItem.ExpirationDate) : null;
        if (nextDay) {
          nextDay.setDate(nextDay.getDate() + 1);
          previousElement.EffectiveDate = nextDay;
        }
      }
      this.workerCompFormArray.value.splice(itemIndexForModify, 1, tableItem);
    }
    this.sortInsuranceDates(this.workerCompFormArray.value);
  }

  private showUndoMessage(insertIndex: number, itemToPaste: RelEmployerItem): Observable<void> {
    if (insertIndex === -1) {
      return EMPTY;
    }

    this.snackBar.dismiss();
    const snackBarRef = this.snackBar.open('The item was Deleted', 'Undo', {
      duration: 6000,
      horizontalPosition: 'center',
      verticalPosition: 'bottom',
    });

    return snackBarRef.onAction().pipe(
      tap(() => this.onUndoClicked(insertIndex, itemToPaste)),
    );
  }

  onUndoClicked(insertIndex: number, itemToPaste: RelEmployerItem): void {
    this.workerCompFormArray.insert(insertIndex, itemToPaste as FormGroup);
    this.choseTableItem(insertIndex);
    this.sortInsuranceDates(this.workerCompFormArray.value);
  }

  public onDeleteTableItem(event: MouseEvent, index: number): void {
    event.stopPropagation();
    const deletedFormGroup = this.workerCompFormArray.at(index);

    this.workerCompFormArray.removeAt(index);
    if (!this.workerCompFormArray.controls.length) {
      this.selectedFormGroup = new FormGroup({});
      this.selectedIndex = null;
      this.addressListItem = {};
    } else {
      const newIndex = index - 1;
      if (newIndex < 0 && this.workerCompFormArray.controls.length > 0) {
        this.choseTableItem(0);
      } else {
        this.choseTableItem(index - 1);
      }
    }

    this.showUndoMessage(index, deletedFormGroup as RelEmployerItem)
      .pipe(
        this.takeUntilDestroy(),
      ).subscribe();
    this.sortInsuranceDates(this.workerCompFormArray.value);
  }

  public onEditTableItem(event: MouseEvent, tableItem: RelEmployerItem): void {
    event.stopPropagation();
    const initialState = {
      mode: FcItemModalMode.Modify,
      itemForModify: tableItem
    };

    this.apModalService.showComponentModal(FcNewInsurancePeriodListModalComponent, {
      panelClass: ['emds-modal-medium', 'emds-modal-medium-fixed'],
      disableClose: true,
      initialState
    }).then((res: ResultModalItem) => {
      if (!!res.resultValue.result) {
        this.setDataItems(res, true);
      }
    });
  }

  public onNewClick(): void {
    const initialState = {
      mode: FcItemModalMode.New,
      dataItems: this.workerCompFormArray.value
    };
    this.apModalService.showComponentModal(FcNewInsurancePeriodListModalComponent, {
      panelClass: ['emds-modal-medium', 'emds-modal-medium-fixed'],
      disableClose: true,
      initialState
    }).then((res: ResultModalItem) => {
      if (!!res.resultValue.result) {
        this.setDataItems(res);
      }
    });
  }

  public getFindControlValidation(controlName: string): boolean {
    if (this.fcListValidation.validations[controlName]) {
      return !!this.fcListValidation.validations[controlName]?.IsRequired;
    } else {
      return !!this.listItemDefaultValidation[controlName]?.IsRequired;
    }
  }

  private disableAddressGroup(): void {
    this.disableAddressListControls.next([true, false]);
  }

  private sortInsuranceDates(insurances: RelEmployerItem[]): void {
    // Sort in descending order
    insurances
      .sort((a, b) => new Date(a?.EffectiveDate)?.getTime() - new Date(b?.EffectiveDate)?.getTime());

    for (let i = 0; i < insurances.length; i++) {
      const currentEffective = new Date(insurances[i]?.EffectiveDate);
      const currentExpiration = new Date(insurances[i]?.ExpirationDate);

      if (i < insurances.length - 1) { // if there is a next item
        const nextEffective = new Date(insurances[i + 1].EffectiveDate);
        const expiration = new Date(nextEffective);

        expiration.setDate(expiration.getDate() - 1);

        if (currentExpiration && currentEffective.getTime() === currentExpiration.getTime()) {
          const newEffective = new Date(currentEffective);
          newEffective.setDate(newEffective.getDate() - 1);
          insurances[i].EffectiveDate = newEffective;
        }

        if (currentEffective <= expiration) {
          insurances[i].ExpirationDate = expiration;
        } else {
          insurances[i].ExpirationDate = currentEffective;
        }
      } else {
        insurances[i].ExpirationDate = null; // Set expiration date to null for the last insurance
      }
    }

    this.workerCompFormArray.patchValue(insurances.reverse(), { emitEvent: false });
  }

  public getWorkerCompControl(index: number, controlName: string): FormControl<string[]> {
    return this.workerCompFormArray.at(index)?.get(controlName) as FormControl<string[]> ?? new FormControl<string[]>([]);
  }

  public getWorkerCompFormGroup(index: number): FormGroup {
    return this.workerCompFormArray.at(index) as FormGroup ?? new FormGroup({});
  }

  public setSelectedIndex(index: number): void {
    this.selectedIndex = index;
    this.selectedFormGroup = this.workerCompFormArray.at(index) as FormGroup ?? new FormGroup({});
    this.addressListItem = this.selectedFormGroup.get('addressData')?.value ?? {};
  }
}
